import type React from "react";
import {observer} from "mobx-react-lite";
import {useState} from "react";
import {Box, Button, Skeleton, Stack, Typography} from "@mui/material";
import SettingsIcon from "~/icons/svg/SettingsIcon.svg?react";
import type {SegmentationScore} from "~/pages/supplier-360/types/Supplier360Types";
import {useSupplierSegmentationQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierSegmentationQuery";
import {authStore} from "~/stores/init";
import {COLORS} from "~/style/colors-ts";
import {
    formatSegmentationDate,
    SEGMENT_COLOR,
    SEGMENT_LABEL,
    SEGMENT_MATRIX_ORDER,
} from "./kraljicSegments";
import {SegmentationScoreDialog} from "./SegmentationScoreDialog";


/** The 2×2 Kraljic grid with this supplier's quadrant lit up — the design's `sg-mini`. */
const SegmentMiniMatrix: React.FC<{segment: SegmentationScore['segment']}> = ({segment}) => (
    <Stack direction="row" gap={0.75} sx={{width: '100%', height: '100%', flex: 1}}>
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase'}}
        >
            Supply risk →
        </Typography>
        <Stack gap={0.75} sx={{flex: 1, height: '100%'}}>
            <Box
                aria-label={`Segmentation matrix — this supplier sits in ${SEGMENT_LABEL[segment]}`}
                sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, flex: 1}}
            >
                {SEGMENT_MATRIX_ORDER.map(k => (
                    <Box
                        key={k}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            minHeight: 62,
                            p: 0.75,
                            borderRadius: 1,
                            fontSize: 11.5,
                            fontWeight: 600,
                            border: 1,
                            borderColor: k === segment ? SEGMENT_COLOR[k].main : COLORS.grey[100],
                            bgcolor: k === segment ? SEGMENT_COLOR[k].tint : 'transparent',
                            color: k === segment ? SEGMENT_COLOR[k].main : COLORS.grey[200],
                        }}
                    >
                        {SEGMENT_LABEL[k]}
                    </Box>
                ))}
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase'}}
            >
                Spend value →
            </Typography>
        </Stack>
    </Stack>
);

export const SegmentationBlock: React.FC<{entityId: number; supplierName: string}> = observer(({entityId, supplierName}) => {
    const {data: segmentation, isLoading, isError} = useSupplierSegmentationQuery(entityId);
    const [scoreOpen, setScoreOpen] = useState(false);
    const canEdit = authStore.hasEditPermission;

    if (isLoading) {
        return <Skeleton variant="rectangular" height={120} />;
    }

    if (isError) {
        return null;
    }

    return (
        <Stack>
            {segmentation && (
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{mb: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        {`${segmentation.source === 'manual' ? `edited by ${segmentation.scored_by}` : 'Segmentation engine'} · ${formatSegmentationDate(segmentation.scored_at)}`}
                    </Typography>
                    {canEdit && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SettingsIcon width={16} height={16} />}
                            sx={{ml: 'auto'}}
                            onClick={() => setScoreOpen(true)}
                        >
                            Re-score
                        </Button>
                    )}
                </Stack>
            )}

            {segmentation ?
                <SegmentMiniMatrix segment={segmentation.segment} />
                :
                (
                    <Stack
                    alignItems="center"
                    gap={1.25}
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        border: '1px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                    }}
                    >
                        <SettingsIcon width={20} height={20} />
                        <Typography variant="body2" color="grey.800" sx={{maxWidth: 460}}>
                            {canEdit
                                ? 'This supplier has no segmentation yet. Rate the five axes and pick the segment — from Bottleneck through Leverage — to record it.'
                                : 'This supplier has no segmentation yet.'}
                        </Typography>
                        {canEdit && (
                            <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SettingsIcon width={16} height={16} />}
                            onClick={() => setScoreOpen(true)}
                            >
                                Set segmentation
                            </Button>
                        )}
                    </Stack>
                )}

            {canEdit && scoreOpen && (
                <SegmentationScoreDialog
                    entityId={entityId}
                    segmentation={segmentation ?? null}
                    supplierName={supplierName}
                    onClose={() => setScoreOpen(false)}
                />
            )}
        </Stack>
    );
});
